import { addDays, endOfDay, parse, startOfWeek } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CalendarDeleteDate, Flag } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import {
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options/types';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { getNextWeekday } from 'shared/lib/date-fns';
import {
  DateFormatOptions,
  dateFormatOptionToLabelMap,
} from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { useDateInput } from 'shared/utils/use-date-input';

const defaultDatePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.NextWeek,
];

const presetLabelMap: Record<DatePresets, string> = {
  [DatePresets.Today]: 'forms.deadline.fields.today.label',
  [DatePresets.Tomorrow]: 'forms.deadline.fields.tomorrow.label',
  [DatePresets.Friday]: 'forms.deadline.fields.friday.label',
  [DatePresets.NextWeek]: 'forms.deadline.fields.next-week.label',
};

export type DatepickerWithOptionsProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children'
> & {
  value: Timestamp | undefined;
  onChange: (date?: Timestamp) => void;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  datePresets?: DatePresets[];
  icon?: IconOptions;
  canRemoveValue?: boolean;
  removeLabel?: string;
};

export const DatepickerWithOptions: React.FC<DatepickerWithOptionsProps> = ({
  value,
  onChange,
  dateFormat,
  weekStartsOn,
  datePresets = defaultDatePresets,
  icon = IconOptions.Calendar,
  canRemoveValue = true,
  removeLabel,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const today = useToday();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useDateInput(value, dateFormat);

  const [inputError, setInputError] = useState(false);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e);
    setInputError(false);
  };

  const changeToPreset = (preset: DatePresets) => {
    switch (preset) {
      case DatePresets.Today:
        onChange(endOfDay(new Date()));
        break;
      case DatePresets.Tomorrow:
        onChange(endOfDay(addDays(new Date(), 1)));
        break;
      case DatePresets.Friday:
        // Get the next Friday from today; Friday is 5 in weekdaysNumberMap
        onChange(endOfDay(getNextWeekday(new Date(), 5)));
        break;
      case DatePresets.NextWeek:
        onChange(
          endOfDay(
            startOfWeek(addDays(new Date(), 7), {
              weekStartsOn: weekdaysNumberMap[weekStartsOn],
            }),
          ),
        );
        break;
      default:
        break;
    }
  };

  const clear = () => onChange(undefined);

  const onChangeDate = (date: Date | null) =>
    onChange(date ? endOfDay(date) : undefined);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useKeysDown(
    ['Enter'],
    () => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        const inputVal = inputRef.current.value;
        const date = inputVal
          ? parse(inputVal, dateFormat, new Date())
          : undefined;
        const isValidDate =
          !inputRef.current.value ||
          (inputVal?.length === dateFormat.length &&
            !!date &&
            !isNaN(date.getTime()));

        if (isValidDate) {
          onChange(date);
          return;
        }

        setInputError(true);
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <PopupMenu {...rest}>
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            iconColor={inputError ? Colors.Error : undefined}
            startIcon={icon === IconOptions.Calendar ? Calendar : Flag}
            textColor={inputError ? Colors.Error : undefined}
            value={inputValue}
            onChange={onChangeInput}
            placeholder={dateFormatOptionToLabelMap[dateFormat]}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      {!!datePresets.length && (
        <PopupMenuList hasBottomBorder>
          {datePresets.map((preset) => {
            const _onClick = () => changeToPreset(preset);

            return (
              <PopupMenuListItem key={preset}>
                <PopupMenuButton
                  onClick={_onClick}
                  start={
                    <Icon
                      icon={icon === IconOptions.Calendar ? Calendar : Flag}
                    />
                  }
                  tabIndex={0}
                  type="button"
                >
                  {t(presetLabelMap[preset])}
                </PopupMenuButton>
              </PopupMenuListItem>
            );
          })}
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder={!!value}>
        <Datepicker
          today={today}
          selected={value}
          onChange={onChangeDate}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          inline
        />
      </PopupMenuList>

      {!!value && canRemoveValue && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={clear}
              start={<Icon icon={CalendarDeleteDate} />}
              textColor={Colors.Error}
              startColor={Colors.Error}
              tabIndex={0}
              type="button"
            >
              {removeLabel ?? t('forms.deadline.fields.clear.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
    </PopupMenu>
  );
};
